/** 28.09 (владелец): «пора сделать функционал админа зала. Администратор зала на филиале видит
 *  все данные об играх в этот день на этом филиале и расстановку операторов (кто, какие
 *  мастер-классы ведёт). Логично расположить в дашборде оператора на этот день».
 *  Позже в тот же день: стандартная «информация о клиенте» с комментариями ДК; перестановки,
 *  добавление, МК — через одобрение ОО, с историей; «как рассчитались игроки».
 *  Блок заменяет прежнюю строку «◆ Админ зала — филиал · план · отчитаться». */
import { useState } from "react";
import type { SideDuties } from "../../hooks/useOperatorDay";
import { useHallDay, hallRoster, type HallGame } from "../../hooks/useHallDay";
import { HallGameCard } from "./HallGameCard";
import { HallSheets, type Sheet } from "./HallSheets";
import { HallPaySheet } from "./HallPaySheet";
import { HallRequests } from "./HallRequests";
import { GameQrPanel } from "../../components/GameQrPanel";
import h from "./hallAdmin.module.css";

type Css = Readonly<Record<string, string>>;
type Admin = NonNullable<SideDuties["admin"]>;

/** 1 игра · 2 игры · 5 игр */
const gamesWord = (n: number) => (n % 10 === 1 && n % 100 !== 11 ? "игра" : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? "игры" : "игр");

export function HallAdminDay({ date, admin, css, onReport }: { date: Date; admin: Admin; css: Css; onReport: (init?: string) => void }) {
  const { day, extra, error, accept, request, cancel, pay } = useHallDay(date, admin.branch);
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [payFor, setPayFor] = useState<HallGame | null>(null);
  const [qrFor, setQrFor] = useState<number | null>(null);   // 26.10: персональный QR игры админа зала (GameQrPanel, как у оператора)
  const games = day?.games ?? [];
  const roster = hallRoster(games);
  const plan = admin.from && admin.to ? `${admin.from}–${admin.to}` : "";
  const fact = admin.repFrom && admin.repTo ? `${admin.repFrom}–${admin.repTo}` : "";
  const reqs = extra?.requests ?? [];
  const pending = reqs.filter((r) => r.status === "pending");
  /* правки — у админа этого дня, пока смена не прошла; прошлые дни — только смотреть */
  const editable = !!day?.me?.sent && fmt(date) >= shiftToday();
  const nameOf = (id: string | null) => (id ? extra?.pool.find((p) => p.id === id)?.name ?? id : "");
  return (
    <section className={h.box} aria-label="Админ зала">
      <div className={h.head}>
        <span className={h.title}>◆ Админ зала</span>
        <span className={h.branch}>{day?.branch.name ?? admin.branch.toUpperCase()}</span>
        {pending.length ? <span className={h.pendPill} title="Заявки ждут ОО">ждут ОО · {pending.length}</span> : null}
        {day?.me?.sent && !day.me.accepted ? (
          <button type="button" className={h.accept} disabled={busy}
            onClick={() => { setBusy(true); void accept().finally(() => setBusy(false)); }}>✓ Принять</button>
        ) : day?.me?.accepted ? <span className={h.accepted}>принято ✓</span> : null}
      </div>
      <div className={`${css.sideRow} ${css.sideTap}`} role="button" title="Тап — отчёт: с какого по какое был"
        onClick={() => onReport(fact || plan || undefined)}>
        {plan ? <>план <b>{plan}</b></> : "часы не заданы"}
        {fact ? <span className={css.sideFact}> · факт {fact} ✓</span> : <span className={css.sideHint}> · отчитаться ›</span>}
      </div>
      {error ? <div className={h.note}>{error}</div> : null}
      {roster.length ? (
        <div className={h.roster}>
          <span className={h.lbl}>На филиале</span>
          {roster.map((r) => (
            <span key={r.name} className={h.who}>
              <b>{r.name}</b>{r.games ? ` · ${r.games} ${gamesWord(r.games)}` : ""}{r.mcs.length ? ` · МК ${r.mcs.join(", ")}` : ""}
            </span>
          ))}
        </div>
      ) : null}
      {day ? (
        <button type="button" className={h.toggle} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          Игры филиала <span className={h.cnt}>({games.length})</span> {open ? "▴" : "▾"}
        </button>
      ) : null}
      {open && day ? (games.length ? games.map((g) => (
        <HallGameCard key={g.booking_id} g={g} pending={pending} payment={extra?.payments[String(g.booking_id)]} editable={editable} nameOf={nameOf} act={{
          onCrew: (op) => setSheet({ t: "crew", g, op }), onAdd: () => setSheet({ t: "add", g }),
          onMcOp: (mc, op) => setSheet({ t: "mcOp", g, mc, op }), onMcAdd: (mc) => setSheet({ t: "mcAdd", g, mc }), onMcNew: () => setSheet({ t: "mcNew", g }),
          onPay: () => setPayFor(g), onQr: () => setQrFor(g.booking_id),
        }} />
      )) : <div className={h.note}>Игр на этот день нет</div>) : null}
      {reqs.length ? <HallRequests reqs={reqs} onCancel={(id) => void cancel(id)} /> : null}
      {day ? <HallSheets sheet={sheet} set={setSheet} day={day} extra={extra} onSend={request} /> : null}
      {qrFor != null ? <GameQrPanel bookingId={qrFor} onClose={() => setQrFor(null)} /> : null}
      {payFor ? <HallPaySheet g={payFor} payment={extra?.payments[String(payFor.booking_id)]} readOnly={!editable} onSave={(b) => pay(payFor.booking_id, b)} onClose={() => setPayFor(null)} /> : null}
    </section>
  );
}

const fmt = (d: Date) => d.toISOString().slice(0, 10);
/** Смена дня D идёт до 06:00 D+1 (Оренбург UTC+5): «сегодняшняя смена» по часам смены. */
const shiftToday = () => new Date(Date.now() - 3_600_000).toISOString().slice(0, 10);
